import { useEffect, useMemo, useState } from "react";
import { Checkbox, Form, Input, Select } from "antd";
import { useAppDispatch, useAppSelector } from "../../../utils/hooks/redux";
import { sortOptionsAlphabetically } from "../../../utils/helpers/sortOptionsAlphabetically";
import StyledModal from "../../../components/CustomModal";
import { StyledButton } from "../../../components/styles";
import { fetchFoundItemDiscardReasons } from "../../../redux/features/foundItemDiscardReason/foundItemDiscardReasonAsyncThunk";
import { RequestResults } from "../../../redux/common/RequestStatus";
import { updateFoundItemStatus } from "../../../redux/features/foundReport/foundReportAsyncThunk";
import { removeTicketById } from "../../../redux/features/board/columnsSlice";
import { useNavigate } from "react-router-dom";
import SystemPayRoutes from "../../../utils/constants/Routes";
import { FoundStatuses } from "../../../types/FoundItemTypes";

type FieldType = {
    reasonForDeletion?: string;
};

interface DiscardFoundItemWithReasonProps {
    isOpen: boolean;
    onClose: () => void;
    selectedRecord: any;
}

const DiscardFoundItemWithReason = ({ isOpen, onClose, selectedRecord }: DiscardFoundItemWithReasonProps) => {
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();
    const { data: deleteReasonData, status } = useAppSelector((state) => state.foundItemDiscardReason);
    const navigate = useNavigate();

    useEffect(() => {
        if (status === RequestResults.idle) {
            dispatch(fetchFoundItemDiscardReasons());
        }
    }, [dispatch, status]);
    const selectedDeleteReason = Form.useWatch("discardReason", form);

    const onFinish = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            const requestPayload = {
                reason: values?.discardReason?.toLocaleLowerCase() === "other" ? values?.discardReasonText : values?.discardReason,
                id: selectedRecord.id?.toString() ?? "",
                status: FoundStatuses.Discarded,
                sendEmail: values?.sendEmail ?? true
            }
            await dispatch(updateFoundItemStatus({ ...requestPayload }));
            await dispatch(removeTicketById({ id: selectedRecord.id?.toString() ?? "" }));
            form.resetFields();
            navigate(SystemPayRoutes.reports);
            onClose();
        } catch (error) {
        } finally {
            setLoading(false);
        }
    };

    const deleteReasonOptions = useMemo(() => {
        return deleteReasonData?.map((deleteReason) => ({
            label: deleteReason.reason ?? "",
            value: deleteReason.reason ?? "",
            key: deleteReason.id?.toString() ?? "",
        }));
    }, [deleteReasonData]);

    const handleClose = () => {
        form.resetFields();
        onClose();
    };

    return (
        <StyledModal
            draggable={true}
            footer={
                <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", width: "100%" }}>
                    <StyledButton onClick={handleClose} disabled={loading}>Cancel</StyledButton>
                    <StyledButton type="primary" onClick={() => onFinish()} loading={loading} disabled={loading}>
                        Discard
                    </StyledButton>
                </div>
            }
            open={isOpen}
            onCancel={() => onClose()}
            title="Are you sure you want discard?"
        >
            <Form form={form} onFinish={onFinish} autoComplete="off" layout="vertical" style={{ marginTop: "20px" }}>
                <Form.Item name="discardReason" label="Discard Reason" rules={[{ required: true, message: "Please select reason" }]}>
                    <Select
                        placeholder={"Select Reason for discard"}
                        options={sortOptionsAlphabetically(deleteReasonOptions, "label")}
                    />
                </Form.Item>

                {selectedDeleteReason?.toLowerCase() === "other" && (
                    <Form.Item name="discardReasonText" label="Additional Information" rules={[{ required: true, message: "Please enter reason" }]}>
                        <Input.TextArea placeholder="Additional Information" name="discardReasonText" />
                    </Form.Item>
                )}

                <Form.Item name="sendEmail" valuePropName="checked" initialValue={true}>
                    <Checkbox>
                        Send email notification
                    </Checkbox>
                </Form.Item>

                <Form.Item
                    name="discard"
                    valuePropName="checked"
                    rules={[
                        {
                            validator(_, value) {
                                if (value) {
                                    return Promise.resolve();
                                }
                                return Promise.reject(new Error("Please check the box to confirm discard."));
                            },
                        },
                    ]}
                >
                    <Checkbox id="delete">
                        I confirm that I want to discard
                    </Checkbox>
                </Form.Item>
            </Form>
        </StyledModal>
    );
};

export default DiscardFoundItemWithReason;
